'use client';

import React, { useState, useEffect } from 'react';
import MarketOverviewCard from './MarketOverviewCard';
import OrderBookPanel from './OrderBookPanel';
import TradingForm from './TradingForm';
import PortfolioSummary from './PortfolioSummary';
import PriceChart from './PriceChart';
import WatchlistPanel from './WatchlistPanel';
import TransactionHistory from './TransactionHistory';
import PriceAlerts from './PriceAlerts';
import Icon from '@/components/ui/AppIcon';
import { fetchKitcoPrices, type KitcoPrice } from '@/utils/kitcoApi';

interface MarketData {
  metal: string;
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  high24h: number;
  low24h: number;
  volume: string;
  icon: string;
  color: string;
}

interface OrderBookEntry {
  price: number;
  amount: number;
  total: number;
}

interface PortfolioHolding {
  metal: string;
  amount: number;
  value: number;
  change: number;
  changePercent: number;
  icon: string;
  color: string;
}

interface ChartDataPoint {
  time: string;
  price: number;
}

interface WatchlistItem {
  id: string;
  metal: string;
  symbol: string;
  price: number;
  change: number;
  changePercent: number;
  icon: string;
}

interface Transaction {
  id: string;
  type: 'buy' | 'sell';
  metal: string;
  amount: number;
  price: number;
  total: number;
  timestamp: string;
  status: 'completed' | 'pending' | 'failed';
}

interface PriceAlert {
  id: string;
  metal: string;
  condition: 'above' | 'below';
  targetPrice: number;
  currentPrice: number;
  isActive: boolean;
}

interface OrderData {
  type: 'buy' | 'sell';
  orderType: 'market' | 'limit';
  amount: number;
  price?: number;
  total: number;
}

const LiveTradingInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [selectedMetal, setSelectedMetal] = useState<string>('Gold');
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');
  const [marketData, setMarketData] = useState<MarketData[]>([
    {
      metal: 'Gold',
      symbol: 'XAU/USD',
      price: 2067.45,
      change: 23.80,
      changePercent: 1.16,
      high24h: 2089.75,
      low24h: 2038.20,
      volume: '1.2M oz',
      icon: 'SparklesIcon',
      color: 'golden-gradient'
    },
    {
      metal: 'Silver',
      symbol: 'XAG/USD',
      price: 24.32,
      change: -0.18,
      changePercent: -0.73,
      high24h: 24.68,
      low24h: 24.15,
      volume: '8.5M oz',
      icon: 'CircleStackIcon',
      color: 'bg-gradient-to-br from-gray-400 to-gray-600'
    },
    {
      metal: 'Platinum',
      symbol: 'XPT/USD',
      price: 912.30,
      change: 8.45,
      changePercent: 0.93,
      high24h: 925.80,
      low24h: 898.50,
      volume: '450K oz',
      icon: 'StarIcon',
      color: 'bg-gradient-to-br from-slate-400 to-slate-700'
    },
    {
      metal: 'Copper',
      symbol: 'HG/USD',
      price: 3.87,
      change: 0.05,
      changePercent: 1.31,
      high24h: 3.92,
      low24h: 3.81,
      volume: '15.2M lbs',
      icon: 'CubeIcon',
      color: 'bg-gradient-to-br from-orange-600 to-orange-800'
    }
  ]);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());
  const [isLoadingPrices, setIsLoadingPrices] = useState(false);

  // NEW: State for dynamic data that updates with prices
  const [chartData, setChartData] = useState<ChartDataPoint[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [portfolioHoldings, setPortfolioHoldings] = useState<PortfolioHolding[]>([]);
  const [buyOrders, setBuyOrders] = useState<OrderBookEntry[]>([]);
  const [sellOrders, setSellOrders] = useState<OrderBookEntry[]>([]);

  // FIXED: Add missing state declarations for watchlist and price alerts
  const [watchlistItems, setWatchlistItems] = useState<WatchlistItem[]>([
    {
      id: 'WL001',
      metal: 'Gold',
      symbol: 'XAU/USD',
      price: 2067.45,
      change: 23.80,
      changePercent: 1.16,
      icon: 'SparklesIcon'
    },
    {
      id: 'WL002',
      metal: 'Silver',
      symbol: 'XAG/USD',
      price: 24.32,
      change: -0.18,
      changePercent: -0.73,
      icon: 'CircleStackIcon'
    },
    {
      id: 'WL003',
      metal: 'Palladium',
      symbol: 'XPD/USD',
      price: 1045.20,
      change: 12.35,
      changePercent: 1.19,
      icon: 'CubeTransparentIcon'
    }
  ]);

  const [priceAlerts, setPriceAlerts] = useState<PriceAlert[]>([
    {
      id: 'ALERT001',
      metal: 'Gold',
      condition: 'above',
      targetPrice: 2100.00,
      currentPrice: 2067.45,
      isActive: true
    },
    {
      id: 'ALERT002',
      metal: 'Silver',
      condition: 'below',
      targetPrice: 24.00,
      currentPrice: 24.32,
      isActive: true
    },
    {
      id: 'ALERT003',
      metal: 'Platinum',
      condition: 'above',
      targetPrice: 950.00,
      currentPrice: 912.30,
      isActive: false
    }
  ]);

  useEffect(() => {
    setIsHydrated(true);
    // Fetch prices immediately on mount
    loadRealTimePrices();
    
    // Set up 8-hour refresh interval (28,800,000 milliseconds)
    const refreshInterval = setInterval(() => {
      loadRealTimePrices();
    }, 8 * 60 * 60 * 1000);

    return () => clearInterval(refreshInterval);
  }, []);

  /**
   * Generates realistic historical chart data based on current price
   */
  const generateChartData = (currentPrice: number): ChartDataPoint[] => {
    const now = new Date();
    const data: ChartDataPoint[] = [];
    
    // Generate 8 data points for the last 8 hours
    for (let i = 7; i >= 0; i--) {
      const time = new Date(now.getTime() - i * 60 * 60 * 1000);
      const timeStr = time.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false });
      
      // Generate price with realistic variation (±2% from current price)
      const variation = (Math.random() - 0.5) * 0.04; // ±2% variation
      const historicalPrice = currentPrice * (1 + variation);
      
      data.push({
        time: timeStr,
        price: parseFloat(historicalPrice.toFixed(2))
      });
    }
    
    // Make the last point exactly the current price
    data[data.length - 1].price = currentPrice;
    
    return data;
  };

  /**
   * Generates dynamic order book based on current price
   */
  const generateOrderBook = (currentPrice: number) => {
    const buyOrders: OrderBookEntry[] = [];
    const sellOrders: OrderBookEntry[] = [];
    
    // Generate 5 buy orders slightly below current price
    for (let i = 0; i < 5; i++) {
      const priceOffset = (i + 1) * 0.25; // $0.25 increments below current
      const orderPrice = currentPrice - priceOffset;
      const amount = parseFloat((Math.random() * 10 + 5).toFixed(1)); // 5-15 oz
      
      buyOrders.push({
        price: parseFloat(orderPrice.toFixed(2)),
        amount: amount,
        total: parseFloat((orderPrice * amount).toFixed(2))
      });
    }
    
    // Generate 5 sell orders slightly above current price
    for (let i = 0; i < 5; i++) {
      const priceOffset = (i + 1) * 0.25; // $0.25 increments above current
      const orderPrice = currentPrice + priceOffset;
      const amount = parseFloat((Math.random() * 10 + 5).toFixed(1)); // 5-15 oz
      
      sellOrders.push({
        price: parseFloat(orderPrice.toFixed(2)),
        amount: amount,
        total: parseFloat((orderPrice * amount).toFixed(2))
      });
    }
    
    setBuyOrders(buyOrders);
    setSellOrders(sellOrders);
  };

  /**
   * Generates realistic transaction history with current prices
   */
  const generateTransactions = (currentGoldPrice: number, currentSilverPrice: number, currentPlatinumPrice: number) => {
    const now = new Date();
    const newTransactions: Transaction[] = [];
    
    // Transaction 1: Recent Gold buy
    const goldBuyAmount = 5.0;
    const goldBuyPrice = currentGoldPrice * 0.999; // Slightly below current
    newTransactions.push({
      id: 'TXN001',
      type: 'buy',
      metal: 'Gold',
      amount: goldBuyAmount,
      price: parseFloat(goldBuyPrice.toFixed(2)),
      total: parseFloat((goldBuyAmount * goldBuyPrice).toFixed(2)),
      timestamp: new Date(now.getTime() - 45 * 60 * 1000).toLocaleString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false 
      }).replace(',', ''),
      status: 'completed'
    });
    
    // Transaction 2: Silver sell
    const silverSellAmount = 50.0;
    const silverSellPrice = currentSilverPrice * 1.002; // Slightly above current
    newTransactions.push({
      id: 'TXN002',
      type: 'sell',
      metal: 'Silver',
      amount: silverSellAmount,
      price: parseFloat(silverSellPrice.toFixed(2)),
      total: parseFloat((silverSellAmount * silverSellPrice).toFixed(2)),
      timestamp: new Date(now.getTime() - 105 * 60 * 1000).toLocaleString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false 
      }).replace(',', ''),
      status: 'completed'
    });
    
    // Transaction 3: Platinum buy pending
    const platinumBuyAmount = 2.5;
    const platinumBuyPrice = currentPlatinumPrice * 0.998;
    newTransactions.push({
      id: 'TXN003',
      type: 'buy',
      metal: 'Platinum',
      amount: platinumBuyAmount,
      price: parseFloat(platinumBuyPrice.toFixed(2)),
      total: parseFloat((platinumBuyAmount * platinumBuyPrice).toFixed(2)),
      timestamp: new Date(now.getTime() - 15 * 60 * 1000).toLocaleString('en-US', { 
        year: 'numeric', 
        month: '2-digit', 
        day: '2-digit', 
        hour: '2-digit', 
        minute: '2-digit', 
        hour12: false 
      }).replace(',', ''),
      status: 'pending'
    });
    
    setTransactions(newTransactions);
  };

  /**
   * Updates portfolio holdings with current market prices
   */
  const updatePortfolioWithCurrentPrices = (currentGoldPrice: number, currentSilverPrice: number, currentPlatinumPrice: number) => {
    const goldAmount = 25.5;
    const silverAmount = 150.0;
    const platinumAmount = 10.0;
    
    // Calculate values at current prices
    const goldValue = goldAmount * currentGoldPrice;
    const silverValue = silverAmount * currentSilverPrice;
    const platinumValue = platinumAmount * currentPlatinumPrice;
    
    // Simulate previous day prices (slightly different)
    const goldPrevPrice = currentGoldPrice * 0.988;
    const silverPrevPrice = currentSilverPrice * 1.007;
    const platinumPrevPrice = currentPlatinumPrice * 0.991;
    
    // Calculate changes
    const goldChange = goldAmount * (currentGoldPrice - goldPrevPrice);
    const silverChange = silverAmount * (currentSilverPrice - silverPrevPrice);
    const platinumChange = platinumAmount * (currentPlatinumPrice - platinumPrevPrice);
    
    const updatedHoldings: PortfolioHolding[] = [
      {
        metal: 'Gold',
        amount: goldAmount,
        value: parseFloat(goldValue.toFixed(2)),
        change: parseFloat(goldChange.toFixed(2)),
        changePercent: parseFloat(((goldChange / (goldAmount * goldPrevPrice)) * 100).toFixed(2)),
        icon: 'SparklesIcon',
        color: 'golden-gradient'
      },
      {
        metal: 'Silver',
        amount: silverAmount,
        value: parseFloat(silverValue.toFixed(2)),
        change: parseFloat(silverChange.toFixed(2)),
        changePercent: parseFloat(((silverChange / (silverAmount * silverPrevPrice)) * 100).toFixed(2)),
        icon: 'CircleStackIcon',
        color: 'bg-gradient-to-br from-gray-400 to-gray-600'
      },
      {
        metal: 'Platinum',
        amount: platinumAmount,
        value: parseFloat(platinumValue.toFixed(2)),
        change: parseFloat(platinumChange.toFixed(2)),
        changePercent: parseFloat(((platinumChange / (platinumAmount * platinumPrevPrice)) * 100).toFixed(2)),
        icon: 'StarIcon',
        color: 'bg-gradient-to-br from-slate-400 to-slate-700'
      }
    ];
    
    setPortfolioHoldings(updatedHoldings);
  };

  /**
   * Fetches real-time prices from metals.dev API and updates market data
   */
  const loadRealTimePrices = async () => {
    setIsLoadingPrices(true);
    console.log('🔄 Fetching real-time prices from metals.dev API...');
    
    try {
      const apiPrices = await fetchKitcoPrices();
      console.log('✅ Received API prices:', apiPrices);
      
      if (apiPrices && apiPrices.length > 0) {
        let updatedGoldPrice = 0;
        let updatedSilverPrice = 0;
        let updatedPlatinumPrice = 0;
        
        // Update market data with API prices
        setMarketData(prevData => 
          prevData.map(metal => {
            const apiPrice = apiPrices.find(
              p => p.symbol === metal.symbol.split('/')[0]
            );
            
            if (apiPrice) {
              console.log(`💰 Updating ${metal.metal} price: $${apiPrice.price.toFixed(2)}`);
              
              // Store prices for other updates
              if (metal.metal === 'Gold') updatedGoldPrice = apiPrice.price;
              if (metal.metal === 'Silver') updatedSilverPrice = apiPrice.price;
              if (metal.metal === 'Platinum') updatedPlatinumPrice = apiPrice.price;
              
              // Calculate 24h high/low based on current price (simplified calculation)
              const priceVariation = apiPrice.price * 0.02; // 2% variation
              
              return {
                ...metal,
                price: apiPrice.price,
                change: apiPrice.change,
                changePercent: apiPrice.changePercent,
                high24h: apiPrice.price + priceVariation,
                low24h: apiPrice.price - priceVariation,
              };
            }
            return metal;
          })
        );
        
        // Update chart data for selected metal (default Gold)
        if (updatedGoldPrice > 0) {
          const newChartData = generateChartData(updatedGoldPrice);
          setChartData(newChartData);
          generateOrderBook(updatedGoldPrice);
        }
        
        // Update transactions with current prices
        if (updatedGoldPrice > 0 && updatedSilverPrice > 0 && updatedPlatinumPrice > 0) {
          generateTransactions(updatedGoldPrice, updatedSilverPrice, updatedPlatinumPrice);
          updatePortfolioWithCurrentPrices(updatedGoldPrice, updatedSilverPrice, updatedPlatinumPrice);
        }
        
        setLastUpdate(new Date());
        console.log('✅ All data updated successfully with current API prices');
        
        setNotificationMessage('✅ All prices and historical data updated from metals.dev API');
        setShowNotification(true);
        setTimeout(() => setShowNotification(false), 3000);
      } else {
        console.warn('⚠️ No prices received from API, using current data');
      }
    } catch (error) {
      console.error('❌ Error loading real-time prices:', error);
      setNotificationMessage('⚠️ Using cached prices - API temporarily unavailable');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 5000);
    } finally {
      setIsLoadingPrices(false);
    }
  };

  const handleOrderSubmit = (orderData: OrderData) => {
    if (!isHydrated) return;
    
    setNotificationMessage(
      `${orderData.type.toUpperCase()} order placed: ${orderData.amount} oz of ${selectedMetal} at ${orderData.total.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
    );
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 5000);
  };

  const handleRemoveFromWatchlist = (id: string) => {
    if (!isHydrated) return;
    setWatchlistItems(watchlistItems.filter(item => item.id !== id));
  };

  const handleToggleAlert = (id: string) => {
    if (!isHydrated) return;
    setPriceAlerts(priceAlerts.map(alert => 
      alert.id === id ? { ...alert, isActive: !alert.isActive } : alert
    ));
  };

  const handleDeleteAlert = (id: string) => {
    if (!isHydrated) return;
    setPriceAlerts(priceAlerts.filter(alert => alert.id !== id));
  };

  const handleAddAlert = () => {
    if (!isHydrated) return;
    setNotificationMessage('Alert creation feature coming soon!');
    setShowNotification(true);
    setTimeout(() => setShowNotification(false), 3000);
  };

  const totalPortfolioValue = portfolioHoldings.reduce((sum, holding) => sum + holding.value, 0);
  const totalPortfolioChange = portfolioHoldings.reduce((sum, holding) => sum + holding.change, 0);
  const totalPortfolioChangePercent = totalPortfolioValue > 0 
    ? (totalPortfolioChange / (totalPortfolioValue - totalPortfolioChange)) * 100 
    : 0;

  const selectedMarketData = marketData.find(m => m.metal === selectedMetal) || marketData[0];

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:540:6" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="540" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:541:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="541" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22animate-pulse%20space-y-6%20p-6%22%7D" className="animate-pulse space-y-6 p-6">
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:542:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="542" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-32%20bg-surface%20rounded-xl%22%7D" className="h-32 bg-surface rounded-xl" />
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:543:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="543" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:544:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="544" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-xl%22%7D" className="h-96 bg-surface rounded-xl" />
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:545:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="545" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-xl%22%7D" className="h-96 bg-surface rounded-xl" />
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:546:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="546" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-96%20bg-surface%20rounded-xl%22%7D" className="h-96 bg-surface rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:554:4" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="554" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22min-h-screen%20bg-background%22%7D" className="min-h-screen bg-background">
      {showNotification && (
        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:556:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="556" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22fixed%20top-20%20right-6%20z-tooltip%20bg-success%20text-success-foreground%20px-6%20py-4%20rounded-lg%20shadow-xl%20flex%20items-center%20space-x-3%20animate-slide-in-from-top%22%7D" className="fixed top-20 right-6 z-tooltip bg-success text-success-foreground px-6 py-4 rounded-lg shadow-xl flex items-center space-x-3 animate-slide-in-from-top">
          <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:557:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="557" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%7D" name="CheckCircleIcon" size={24} variant="solid" />
          <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:558:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="558" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-medium%22%7D" className="font-medium">{notificationMessage}</span>
        </div>
      )}

      <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:562:6" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="562" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-full%20px-4%20lg%3Apx-8%20py-8%20space-y-8%22%7D" className="w-full px-4 lg:px-8 py-8 space-y-8">
        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:563:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="563" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:564:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="564" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D">
            <h1 data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:565:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="565" data-component-file="LiveTradingInteractive.tsx" data-component-name="h1" data-component-content="%7B%22elementName%22%3A%22h1%22%2C%22className%22%3A%22text-3xl%20font-bold%20text-text-primary%20font-accent%20mb-2%22%2C%22textContent%22%3A%22Live%20Trading%20Dashboard%22%7D" className="text-3xl font-bold text-text-primary font-accent mb-2">Live Trading Dashboard</h1>
            <p data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:566:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="566" data-component-file="LiveTradingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-text-secondary%22%2C%22textContent%22%3A%22Real-time%20precious%20metals%20trading%20with%20instant%20execution%22%7D" className="text-text-secondary">Real-time precious metals trading with instant execution</p>
          </div>
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:568:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="568" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:569:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="569" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20bg-success%2F10%20text-success%20rounded-lg%22%7D" className="flex items-center space-x-2 px-4 py-2 bg-success/10 text-success rounded-lg">
              <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:570:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="570" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22w-2%20h-2%20bg-success%20rounded-full%20animate-pulse%22%7D" className="w-2 h-2 bg-success rounded-full animate-pulse" />
              <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:571:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="571" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%2C%22textContent%22%3A%22Market%20Open%22%7D" className="text-sm font-medium">Market Open</span>
            </div>
            <button data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:573:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="573" data-component-file="LiveTradingInteractive.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20px-4%20py-2%20bg-primary%20hover%3Abg-primary%2F90%20text-white%20rounded-lg%20transition-smooth%20disabled%3Aopacity-50%22%7D"
              onClick={loadRealTimePrices}
              disabled={isLoadingPrices}
              className="flex items-center space-x-2 px-4 py-2 bg-primary hover:bg-primary/90 text-white rounded-lg transition-smooth disabled:opacity-50"
              title="Refresh prices from metals.dev API"
            >
              <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:579:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="579" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ArrowPathIcon%22%7D" 
                name="ArrowPathIcon" 
                size={20} 
                variant="outline" 
                className={isLoadingPrices ? 'animate-spin' : ''} 
              />
              <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:585:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="585" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20font-medium%22%2C%22textContent%22%3A%22Refresh%20Prices%22%7D" className="text-sm font-medium">Refresh Prices</span>
            </button>
          </div>
        </div>

        {/* Price update indicator */}
        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:591:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="591" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-card%20border%20border-border%20rounded-lg%20p-3%20flex%20items-center%20justify-between%22%7D" className="bg-card border border-border rounded-lg p-3 flex items-center justify-between">
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:592:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="592" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
            <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:593:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="593" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ClockIcon%22%2C%22className%22%3A%22text-text-secondary%22%7D" name="ClockIcon" size={20} variant="outline" className="text-text-secondary" />
            <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:594:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="594" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-text-secondary%22%2C%22textContent%22%3A%22Last%20updated%3A%20%E2%80%A2%20Next%20auto-refresh%20in%208%20hours%22%7D" className="text-sm text-text-secondary">
              Last updated: {lastUpdate.toLocaleTimeString()} • Next auto-refresh in 8 hours
            </span>
          </div>
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:598:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="598" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20text-sm%22%7D" className="flex items-center space-x-2 text-sm">
            <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:599:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="599" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-success%22%7D" name="CheckCircleIcon" size={16} variant="solid" className="text-success" />
            <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:600:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="600" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-success%20font-medium%22%2C%22textContent%22%3A%22Live%20API%20data%20%E2%80%A2%20All%20prices%20synced%22%7D" className="text-success font-medium">Live API data • All prices synced</span>
          </div>
        </div>

        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:604:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="604" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20lg%3Agrid-cols-4%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {marketData.map((data, index) => (
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:606:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="606" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22cursor-pointer%22%7D" key={index} onClick={() => setSelectedMetal(data.metal)} className="cursor-pointer">
              <MarketOverviewCard data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:607:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="607" data-component-file="LiveTradingInteractive.tsx" data-component-name="MarketOverviewCard" data-component-content="%7B%22elementName%22%3A%22MarketOverviewCard%22%7D" data={data} />
            </div>
          ))}
        </div>

        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:612:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="612" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:613:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="613" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22lg%3Acol-span-2%20space-y-6%22%7D" className="lg:col-span-2 space-y-6">
            <PriceChart data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:614:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="614" data-component-file="LiveTradingInteractive.tsx" data-component-name="PriceChart" data-component-content="%7B%22elementName%22%3A%22PriceChart%22%7D" 
              data={chartData} 
              metal={selectedMetal} 
              currentPrice={selectedMarketData.price} 
            />
            
            <TransactionHistory data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:620:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="620" data-component-file="LiveTradingInteractive.tsx" data-component-name="TransactionHistory" data-component-content="%7B%22elementName%22%3A%22TransactionHistory%22%7D" transactions={transactions} />
          </div>

          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:623:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="623" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
            <TradingForm data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:624:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="624" data-component-file="LiveTradingInteractive.tsx" data-component-name="TradingForm" data-component-content="%7B%22elementName%22%3A%22TradingForm%22%7D" 
              metal={selectedMetal} 
              currentPrice={selectedMarketData.price}
              onSubmit={handleOrderSubmit}
            />
            
            <OrderBookPanel data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:630:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="630" data-component-file="LiveTradingInteractive.tsx" data-component-name="OrderBookPanel" data-component-content="%7B%22elementName%22%3A%22OrderBookPanel%22%7D" 
              buyOrders={buyOrders}
              sellOrders={sellOrders}
              currentPrice={selectedMarketData.price}
            />
          </div>
        </div>

        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:638:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="638" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20lg%3Agrid-cols-3%20gap-6%22%7D" className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <PortfolioSummary data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:639:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="639" data-component-file="LiveTradingInteractive.tsx" data-component-name="PortfolioSummary" data-component-content="%7B%22elementName%22%3A%22PortfolioSummary%22%7D" 
            holdings={portfolioHoldings}
            totalValue={totalPortfolioValue}
            totalChange={totalPortfolioChange}
            totalChangePercent={totalPortfolioChangePercent}
          />
          
          <WatchlistPanel data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:646:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="646" data-component-file="LiveTradingInteractive.tsx" data-component-name="WatchlistPanel" data-component-content="%7B%22elementName%22%3A%22WatchlistPanel%22%7D" 
            items={watchlistItems}
            onRemove={handleRemoveFromWatchlist}
          />
          
          <PriceAlerts data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:651:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="651" data-component-file="LiveTradingInteractive.tsx" data-component-name="PriceAlerts" data-component-content="%7B%22elementName%22%3A%22PriceAlerts%22%7D" 
            alerts={priceAlerts}
            onToggle={handleToggleAlert}
            onDelete={handleDeleteAlert}
            onAdd={handleAddAlert}
          />
        </div>

        <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:659:8" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="659" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-gradient-to-r%20from-primary%2F10%20to-secondary%2F10%20rounded-xl%20p-6%20border%20border-border%22%7D" className="bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl p-6 border border-border">
          <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:660:10" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="660" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20space-x-4%22%7D" className="flex items-start space-x-4">
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:661:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="661" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-12%20h-12%20rounded-lg%20bg-trust%20flex%20items-center%20justify-center%20flex-shrink-0%22%7D" className="w-12 h-12 rounded-lg bg-trust flex items-center justify-center flex-shrink-0">
              <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:662:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="662" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ShieldCheckIcon%22%2C%22className%22%3A%22text-white%22%7D" name="ShieldCheckIcon" size={24} variant="solid" className="text-white" />
            </div>
            <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:664:12" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="664" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%22%7D" className="flex-1">
              <h3 data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:665:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="665" data-component-file="LiveTradingInteractive.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22text-lg%20font-semibold%20text-text-primary%20mb-2%22%2C%22textContent%22%3A%22Secure%20Trading%20Environment%22%7D" className="text-lg font-semibold text-text-primary mb-2">Secure Trading Environment</h3>
              <p data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:666:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="666" data-component-file="LiveTradingInteractive.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-text-secondary%20mb-4%22%2C%22textContent%22%3A%22All%20transactions%20are%20protected%20with%20multi-factor%20authentication%2C%20encrypted%20data%20transmission%2C%20and%20compliance%20with%20international%20financial%20regulations.%20Your%20precious%20metals%20investments%20are%20backed%20by%20verified%20ethical%20sourcing%20and%20complete%20supply%20chain%20transparency.%22%7D" className="text-sm text-text-secondary mb-4">
                All transactions are protected with multi-factor authentication, encrypted data transmission, and compliance with international financial regulations. Your precious metals investments are backed by verified ethical sourcing and complete supply chain transparency.
              </p>
              <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:669:14" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="669" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-3%22%7D" className="flex flex-wrap gap-3">
                <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:670:16" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="670" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20text-sm%20text-text-secondary%22%7D" className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:671:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="671" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22LockClosedIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="LockClosedIcon" size={16} variant="solid" className="text-primary" />
                  <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:672:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="672" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22256-bit%20Encryption%22%7D">256-bit Encryption</span>
                </div>
                <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:674:16" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="674" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20text-sm%20text-text-secondary%22%7D" className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:675:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="675" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckBadgeIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="CheckBadgeIcon" size={16} variant="solid" className="text-primary" />
                  <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:676:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="676" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22LBMA%20Certified%22%7D">LBMA Certified</span>
                </div>
                <div data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:678:16" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="678" data-component-file="LiveTradingInteractive.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%20text-sm%20text-text-secondary%22%7D" className="flex items-center space-x-2 text-sm text-text-secondary">
                  <Icon data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:679:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="679" data-component-file="LiveTradingInteractive.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22DocumentCheckIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="DocumentCheckIcon" size={16} variant="solid" className="text-primary" />
                  <span data-component-id="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx:680:18" data-component-path="src/app/live-trading-dashboard/components/LiveTradingInteractive.tsx" data-component-line="680" data-component-file="LiveTradingInteractive.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Conflict-Free%20Verified%22%7D">Conflict-Free Verified</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveTradingInteractive;